import React, { useState } from 'react';
import {
  Box,
  Button,
  Grid,
  Typography,
  Paper,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import FormInput from '../inputs/FormInput';
import PhoneInput from '../inputs/PhoneInput';

const AddressForm = ({
  address = null,
  onSubmit,
  onCancel,
  loading = false
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    label: address?.label || '',
    country_code: address?.country_code || 'KW',
    city: address?.city || '',
    area: address?.area || '',
    street: address?.street || '',
    building: address?.building || '',
    floor: address?.floor || '',
    apartment: address?.apartment || '',
    phone: address?.phone || '',
    is_default_shipping: address?.is_default_shipping || false,
    is_default_billing: address?.is_default_billing || false,
  });

  // Extract country code and phone number from existing phone
  const extractPhoneInfo = (phoneString) => {
    if (!phoneString) return { countryCode: 'KW', phoneNumber: '' };

    // Remove + and extract country code
    const cleanPhone = phoneString.replace('+', '');

    // Check if it starts with country code
    const egyptCode = '20';
    const kuwaitCode = '965';

    if (cleanPhone.startsWith(egyptCode)) {
      return {
        countryCode: egyptCode,
        phoneNumber: cleanPhone.substring(2)
      };
    } else if (cleanPhone.startsWith(kuwaitCode)) {
      return {
        countryCode: kuwaitCode,
        phoneNumber: cleanPhone.substring(3)
      };
    } else {
      // Default to Kuwait for new addresses
      return {
        countryCode: kuwaitCode,
        phoneNumber: cleanPhone
      };
    }
  };

  const phoneInfo = extractPhoneInfo(address?.phone || '');

  const [selectedCountryCode, setSelectedCountryCode] = useState('20');
  const [phoneNumber, setPhoneNumber] = useState(phoneInfo.phoneNumber);

  const handleChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleSwitchChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.checked
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      phone: `+${selectedCountryCode}${phoneNumber}`
    });
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        {address ? t('address.editAddress', 'Edit Address') : t('address.addAddress', 'Add New Address')}
      </Typography>

      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <FormInput
              type="text"
              label={t('address.addressLabel', 'Address Label')}
              value={formData.label}
              onChange={handleChange('label')}
              placeholder={t('address.addressLabelPlaceholder', 'Home, Work, etc.')}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormInput
              type="text"
              label={t('address.city', 'City')}
              value={formData.city}
              onChange={handleChange('city')}
              required
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormInput
              type="text"
              label={t('address.area', 'Area')}
              value={formData.area}
              onChange={handleChange('area')}
              required
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormInput
              type="text"
              label={t('address.street', 'Street')}
              value={formData.street}
              onChange={handleChange('street')}
              required
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormInput
              type="text"
              label={t('address.building', 'Building')}
              value={formData.building}
              onChange={handleChange('building')}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormInput
              type="text"
              label={t('address.floor', 'Floor')}
              value={formData.floor}
              onChange={handleChange('floor')}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormInput
              type="text"
              label={t('address.apartment', 'Apartment')}
              value={formData.apartment}
              onChange={handleChange('apartment')}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <PhoneInput
              label={t('address.phone', 'Phone')}
              selectedCountryCode={selectedCountryCode}
              setSelectedCountryCode={setSelectedCountryCode}
              phoneNumber={phoneNumber}
              setPhoneNumber={setPhoneNumber}
              required
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormInput
              type="switch"
              label={t('address.defaultShipping', 'Default Shipping Address')}
              name="is_default_shipping"
              checked={formData.is_default_shipping}
              onChange={handleSwitchChange('is_default_shipping')}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormInput
              type="switch"
              label={t('address.defaultBilling', 'Default Billing Address')}
              name="is_default_billing"
              checked={formData.is_default_billing}
              onChange={handleSwitchChange('is_default_billing')}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                sx={{ minWidth: 120 }}
              >
                {loading ? t('common.saving', 'Saving...') :
                  address ? t('common.update', 'Update') : t('common.save', 'Save')}
              </Button>

              <Button
                variant="outlined"
                onClick={onCancel}
                disabled={loading}
                sx={{ minWidth: 120 }}
              >
                {t('common.cancel', 'Cancel')}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
};

export default AddressForm;
